import { AemConfigurationThirdpartySocialLoginProvider, CheckoutErrorMessage, ErrorMessage, EventEnum, GuestLoginResponseCode, HTTPStatusCode, RoutePathname, } from '@as-react/common-types';
import { EventUtil } from '@as-react/common-utils';
import { push } from 'connected-react-router';
import { all, call, fork, getContext, put, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects';
import AuthApi from '../../../api/auth/AuthApi';
import AuthSocialLoginFacebookApi, { FacebookErrorsEnum } from '../../../api/auth/AuthSocialLoginFacebookApi';
import CustomerApi from '../../../api/customer/CustomerApi';
import { BASKET_CHANGED, GUEST_EMAIL } from '../../../constants/StorageKeysConstants';
import { AuthActionTypes, GeneralActionTypes, WishListActionTypes } from '../../../enums/ActionEnums';
import { getLoginErrorMessage } from '../../../util/AuthUtil';
import BasketUtil from '../../../util/BasketUtil';
import SessionStorageUtil from '../../../util/SessionStorageUtil';
import { isLoggedInUser } from '../../../util/SessionUtil';
import { generatePathUrl } from '../../../util/URLParamUtil';
import { basketActions } from '../../basket/actions/BasketActions.creators';
import { wishListActions } from '../../wishList/actions/WishListActions.creators';
import { authActions } from '../actions/AuthActions.creators';
import { analyticsActions } from './../../analytics/actions/AnalyticsActions.creators';
const getInitializedSocialList = state => state.authentication.social.initialized;
const getAuthError = state => state.authentication.error;
const getIsLoggedIn = state => isLoggedInUser(state.session);
function* customEventSaga() {
    yield takeEvery(GeneralActionTypes.CUSTOM_EVENT, handleCustomEvent);
}
function* handleCustomEvent(action) {
    var _a;
    switch (action.payload.eventType) {
        case EventEnum.LATITUDE_USER_LOGGED_IN: // is dispatched from the AEM Latitude component
            yield put(authActions.loginSuccess(((_a = action.payload.data) === null || _a === void 0 ? void 0 : _a.redirectUrl)
                ? { redirectUrl: action.payload.data.redirectUrl }
                : { fetchWishlistProducts: true }));
            break;
        default:
            break;
    }
}
export function* getUserAuthState() {
    try {
        const { servicesEndpoint } = yield getContext('aemConfiguration');
        yield call(AuthApi.isAuthenticated, servicesEndpoint);
        yield put(authActions.updateUserAuthState({ authenticated: true }));
    }
    catch (_a) { }
}
export function* getUserAuthStateSaga() {
    yield takeLatest(AuthActionTypes.GET_USER_AUTH_STATE, getUserAuthState);
}
export function* login(action) {
    try {
        const { servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
        const loginResponseData = yield call(AuthApi.login, servicesEndpoint, defaultRequestParams, action.payload.username, action.payload.password, action.payload.keepSignedIn);
        if (!action.payload.reloadOnSuccess) {
            EventUtil.dispatch(EventEnum.LOGIN_SUCCESS, loginResponseData);
        }
        yield put(authActions.loginSuccess({ reload: action.payload.reloadOnSuccess }));
    }
    catch (error) {
        yield fork(handleLoginError, error, action.payload.location);
    }
}
export function* registeredLogin(action) {
    try {
        const { username, password, keepSignedIn, reloadOnSuccess } = action.payload;
        const { servicesEndpoint, root, defaultRequestParams } = yield getContext('aemConfiguration');
        const loginResponseData = yield call(AuthApi.registeredLogin, servicesEndpoint, defaultRequestParams, username, password, keepSignedIn);
        EventUtil.dispatch(EventEnum.LOGIN_SUCCESS, loginResponseData);
        if (!loginResponseData.data.readyForCheckout) {
            SessionStorageUtil.setItem(BASKET_CHANGED, true);
            location.href = `${root}checkout/basket-overview.html`;
            return;
        }
        yield put(analyticsActions.loginStepOption(false));
        yield put(authActions.loginSuccess({ reload: reloadOnSuccess }));
    }
    catch (error) {
        yield fork(handleLoginError, error, action.payload.location);
    }
}
export function* loginReturnGuest(action) {
    var _a, _b, _c, _d;
    try {
        const { servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
        yield call(AuthApi.loginReturnGuest, servicesEndpoint, defaultRequestParams, action.payload.username, action.payload.orderNumber);
        yield put(authActions.createLoginReturnGuestSuccessAction());
    }
    catch (error) {
        if (((_a = error === null || error === void 0 ? void 0 : error.response.data) === null || _a === void 0 ? void 0 : _a.error_code) === '404' ||
            ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) === HTTPStatusCode.Forbidden ||
            ((_d = (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.code) ===
                GuestLoginResponseCode.VALIDATION) {
            yield put(authActions.createLoginReturnGuestFailAction(ErrorMessage.GUEST_ACCOUNT_DOES_NOT_MATCH));
        }
        else {
            yield put(authActions.createLoginReturnGuestFailAction(ErrorMessage.GENERAL));
        }
    }
}
export function* handleLoginSuccess(action) {
    var _a, _b, _c;
    SessionStorageUtil.removeItem(GUEST_EMAIL);
    if (((_a = action.payload) === null || _a === void 0 ? void 0 : _a.reload) || ((_b = action.payload) === null || _b === void 0 ? void 0 : _b.redirectUrl)) {
        yield put(wishListActions.login());
        yield take([WishListActionTypes.LOGGED_IN, WishListActionTypes.LOGIN_FAILED]);
        if (action.payload.reload) {
            location.reload();
        }
        else if (action.payload.redirectUrl) {
            location.href = action.payload.redirectUrl;
        }
    }
    else {
        yield put(wishListActions.login());
        yield put(basketActions.createGetBasicAction());
        EventUtil.dispatch(EventEnum.USER_LOGGED_IN, { loggedIn: true });
        if ((_c = action.payload) === null || _c === void 0 ? void 0 : _c.fetchWishlistProducts) {
            yield take(WishListActionTypes.LOGGED_IN);
            yield put(wishListActions.getWishListProducts());
        }
    }
}
export function* handleLoginError(error, location) {
    var _a, _b;
    const errorMessage = getLoginErrorMessage(Number((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error_code), location);
    yield put(authActions.loginFail(errorMessage));
}
export function* loginSaga() {
    yield takeLatest(AuthActionTypes.LOGIN, login);
}
export function* loginReturnGuestSaga() {
    yield takeLatest(AuthActionTypes.LOGIN_RETURN_GUEST, loginReturnGuest);
}
export function* registeredLoginSaga() {
    yield takeLatest(AuthActionTypes.REGISTERED_LOGIN, registeredLogin);
}
export function* loginSuccessSaga() {
    yield takeLatest(AuthActionTypes.LOGIN_SUCCESS, handleLoginSuccess);
}
export function* logout() {
    try {
        const { servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
        yield call(AuthApi.logout, servicesEndpoint, defaultRequestParams);
        if (AuthSocialLoginFacebookApi.isInitialized()) {
            const isAuthenticated = yield call(AuthSocialLoginFacebookApi.isAuthenticated);
            if (isAuthenticated) {
                yield call(AuthSocialLoginFacebookApi.logoutWithFacebook);
            }
        }
        yield put(authActions.logoutSuccess());
        EventUtil.dispatch(EventEnum.USER_LOGGED_OUT);
    }
    catch (_a) { }
}
export function* logoutSaga() {
    yield takeLatest(AuthActionTypes.LOGOUT, logout);
}
export function* resetCredentials(action) {
    var _a, _b;
    try {
        const { servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
        yield call(CustomerApi.sendPasswordResetMail, servicesEndpoint, defaultRequestParams, action.payload);
        yield put(authActions.createResetCredentialsSuccessAction('navigation:flyout.account.succes.operation'));
    }
    catch (error) {
        let errorMessage = 'navigation:flyout.account.error.operation.mail';
        if (((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error_code) === '404') {
            errorMessage = 'navigation:flyout.account.error.email.unknown.user';
        }
        yield put(authActions.createResetCredentialsErrorAction(errorMessage));
    }
}
export function* resetCredentialsSaga() {
    yield takeLatest(AuthActionTypes.RESET_PASSWORD, resetCredentials);
}
export function* loadSocialSaga() {
    yield takeLatest(AuthActionTypes.INIT_SOCIAL, loadSocial);
}
export function* loadSocial() {
    try {
        const initialized = yield select(getInitializedSocialList);
        const aemConfigurationContext = yield getContext('aemConfiguration');
        const enabledList = aemConfigurationContext.thirdparty.socialLogin.enabledList;
        const loadList = enabledList.filter((item) => !initialized.includes(item));
        const socialAuthObject = {
            [AemConfigurationThirdpartySocialLoginProvider.FACEBOOK]: loadSocialAuthFacebook,
        };
        yield all(loadList
            .filter((item) => !!socialAuthObject[item])
            .map((item) => spawn(socialAuthObject[item])));
    }
    catch (_a) { }
}
export function* loadSocialAuthFacebook() {
    try {
        const aemConfigurationContext = yield getContext('aemConfiguration');
        const facebookConfig = aemConfigurationContext.thirdparty.facebook;
        const { pageInfo, locale, servicesEndpoint, defaultRequestParams } = aemConfigurationContext;
        const { isBuyPage } = pageInfo;
        AuthSocialLoginFacebookApi.setApiPrefix(servicesEndpoint);
        AuthSocialLoginFacebookApi.setDefaultRequestParams(defaultRequestParams);
        AuthSocialLoginFacebookApi.setLocale(locale);
        AuthSocialLoginFacebookApi.setIsBuyPage(isBuyPage);
        AuthSocialLoginFacebookApi.setFacebookApplicationId(facebookConfig.appId);
        yield call(AuthSocialLoginFacebookApi.initializeSDK);
        yield put(authActions.createLoadSocialSuccessAction(AemConfigurationThirdpartySocialLoginProvider.FACEBOOK));
    }
    catch (_a) { }
}
export function* socialLoginSaga() {
    yield takeLatest(AuthActionTypes.SOCIAL_LOGIN, socialLogin);
}
export function* socialLogin(action) {
    switch (action.payload) {
        case AemConfigurationThirdpartySocialLoginProvider.FACEBOOK:
            yield fork(socialLoginFacebook);
            break;
        default:
            break;
    }
}
export function* socialLoginFacebook() {
    try {
        const authError = yield select(getAuthError);
        const isLoggedIn = yield select(getIsLoggedIn);
        const isAuthenticatedOnFacebook = yield call(AuthSocialLoginFacebookApi.isAuthenticated);
        if (authError === FacebookErrorsEnum.PERMISSION_EMAIL) {
            const loggedIn = yield call(AuthSocialLoginFacebookApi.updatePermissions);
            EventUtil.dispatch(EventEnum.USER_LOGGED_IN, { loggedIn });
        }
        else if (!isAuthenticatedOnFacebook) {
            const loggedIn = yield call(AuthSocialLoginFacebookApi.login);
            EventUtil.dispatch(EventEnum.USER_LOGGED_IN, { loggedIn });
        }
        else if (isAuthenticatedOnFacebook && !isLoggedIn) {
            yield call(AuthSocialLoginFacebookApi.logoutWithFacebook);
            const loggedIn = yield call(AuthSocialLoginFacebookApi.login);
            EventUtil.dispatch(EventEnum.USER_LOGGED_IN, { loggedIn });
        }
    }
    catch (e) {
        yield put(authActions.loginFail(e.message));
    }
}
export function* loginGuestSaga() {
    yield takeLatest(AuthActionTypes.LOGIN_GUEST, loginGuest);
}
export function* loginGuest(action) {
    const aemConfigurationContext = yield getContext('aemConfiguration');
    const { isShopLanding, servicesEndpoint, defaultRequestParams } = aemConfigurationContext;
    const isKnownCustomerCard = action.payload.exploreMoreCardNumber && action.payload.customerUUID;
    try {
        if (isKnownCustomerCard) {
            yield call(CustomerApi.updateCardWithEmail, servicesEndpoint, defaultRequestParams, action.payload.userName, action.payload.customerUUID);
        }
        yield call(AuthApi.loginGuest, servicesEndpoint, defaultRequestParams, action.payload.userName, action.payload.exploreMoreCardNumber);
        yield put(analyticsActions.loginStepOption(true));
        yield put(authActions.createLoginGuestSuccessAction());
        if (!isShopLanding) {
            SessionStorageUtil.setItem(GUEST_EMAIL, action.payload.userName);
        }
    }
    catch (error) {
        yield fork(handleLoginGuestError, error);
        if (!isShopLanding) {
            SessionStorageUtil.removeItem(GUEST_EMAIL);
        }
    }
}
export function* loginGuestSuccessSaga() {
    yield takeLatest(AuthActionTypes.LOGIN_GUEST_SUCCESS, handleLoginGuestSuccess);
}
export function* handleLoginGuestSuccess() {
    const { isInAuthorMode, root, isShopLanding, feature_toggles } = yield getContext('aemConfiguration');
    const isNewAuth = feature_toggles.use_new_auth_flow;
    yield put(push(generatePathUrl(root, `buy.html/${RoutePathname.DELIVERY}`, isInAuthorMode)));
    yield put(basketActions.createGetBasicAction());
    if (!isShopLanding) {
        yield put(wishListActions.login());
    }
    if (!isNewAuth) {
        EventUtil.dispatch(EventEnum.USER_LOGGED_IN, { loggedIn: true });
    }
}
export function* handleLoginGuestError(error) {
    var _a;
    const { root } = yield getContext('aemConfiguration');
    let guestErrorMessage;
    if (BasketUtil.isBasketNotInitialized(error)) {
        location.href = `${root}checkout/basket-overview.html`;
        return;
    }
    if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.data.error) === CheckoutErrorMessage.INVALID_EMAIL) {
        guestErrorMessage = ErrorMessage.INVALID_EMAIL;
    }
    else {
        guestErrorMessage = ErrorMessage.API_ERROR;
    }
    yield put(authActions.createLoginGuestFailAction(guestErrorMessage));
}
export default function* authenticationSaga() {
    yield all([
        customEventSaga(),
        getUserAuthStateSaga(),
        loginSaga(),
        registeredLoginSaga(),
        loginSuccessSaga(),
        logoutSaga(),
        resetCredentialsSaga(),
        loadSocialSaga(),
        socialLoginSaga(),
        loginGuestSaga(),
        loginGuestSuccessSaga(),
        loginReturnGuestSaga(),
    ]);
}
